import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as loginService } from '../services/authService';
import { userService } from '../services/userService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// User roles
export const USER_ROLES = {
  ADMIN: 'Admin',
  SEMI_ADMIN: 'Semi Admin', 
  AGENT: 'Agent',
  STUDENT: 'Student',
  INTERVIEWER: 'Interviewer',
  RELATIONSHIP_MANAGER: 'Relationship Manager'
};

// Permissions
export const PERMISSIONS = {
  MANAGE_USERS: 'manage_users',
  MANAGE_COMMISSIONS: 'manage_commissions',
  MANAGE_SEMI_ADMINS: 'manage_semi_admins',
  MANAGE_UNIVERSITIES: 'manage_universities',
  MANAGE_AGENTS: 'manage_agents',
  ACCESS_ADMIN_SETTINGS: 'access_admin_settings',
  VIEW_AUDIT_LOGS: 'view_audit_logs',
  VIEW_ALL_APPLICATIONS: 'view_all_applications',
  MANAGE_APPLICATIONS: 'manage_applications',
  UPLOAD_DOCUMENTS: 'upload_documents',
  ASSIGN_STUDENTS: 'assign_students',
  VIEW_FINANCIAL_REPORTS: 'view_financial_reports',
  VIEW_COMMISSION_STRUCTURE: 'view_commission_structure',
  ADD_COMMENTS: 'add_comments',
  TRACK_STUDENT_PROGRESS: 'track_student_progress',
  VIEW_OWN_APPLICATIONS: 'view_own_applications',
  SUBMIT_APPLICATIONS: 'submit_applications',
  VIEW_ASSIGNED_STUDENTS: 'view_assigned_students',
  MANAGE_OWN_APPLICATIONS: 'manage_own_applications',
  UPLOAD_OWN_DOCUMENTS: 'upload_own_documents',
  VIEW_OWN_TRANSACTIONS: 'view_own_transactions'
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = () => {
      try {
        // Check localStorage first (Remember Me enabled), then sessionStorage
        let token = localStorage.getItem('token');
        let userData = localStorage.getItem('user');
        let storageType = 'localStorage';
        
        // If not in localStorage, check sessionStorage
        if (!token || !userData) {
          token = sessionStorage.getItem('token');
          userData = sessionStorage.getItem('user');
          storageType = 'sessionStorage';
        }
        
        if (token && userData) {
          const user = JSON.parse(userData);
          // Check if user is approved (admin, semi-admin, interviewer, and relationship manager users are always approved)
          if (user.isApproved || user.role === 'Admin' || user.role === 'Semi Admin' || user.role === 'Interviewer' || user.role === 'Relationship Manager') {
            setUser(user);
            setIsAuthenticated(true);
        
          } else {
            // User not approved, clear storage
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
          }
        }
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Login function
  const login = (userData, token, rememberMe = false) => {
    setUser(userData);
    setIsAuthenticated(true);
    
    // Store token and user data based on "Remember Me" setting
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem('token', token);
    storage.setItem('user', JSON.stringify(userData));
    

  };

  // Keep user profile in sync after authentication
  useEffect(() => {
    const syncProfile = async () => {
      try {
        if (!isAuthenticated) return;
        const resp = await userService.getProfile();
        if (resp?.user) {
          setUser(resp.user);
          // Persist the freshest user profile to whichever storage currently holds the session
          const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
          storage.setItem('user', JSON.stringify(resp.user));
        }
      } catch (e) {
        // ignore sync failures
      }
    };
    syncProfile();
  }, [isAuthenticated]);

  // Update user data
  const updateUser = (userData) => {
    // Preserve existing user data and merge with new data
    const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');
    const updatedUser = { ...currentUser, ...userData };
    

    
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const hasPermission = (permission) => {
    return user?.permissions?.includes(permission) || false;
  };

  const hasAnyPermission = (permissions) => {
    return permissions.some(p => hasPermission(p));
  };

  const hasAllPermissions = (permissions) => {
    return permissions.every(p => hasPermission(p));
  };

  const hasRole = (role) => user?.role === role;
  const isAdmin = () => hasRole(USER_ROLES.ADMIN);
  const isSemiAdmin = () => hasRole(USER_ROLES.SEMI_ADMIN);
  const isAgent = () => hasRole(USER_ROLES.AGENT);
  const isStudent = () => hasRole(USER_ROLES.STUDENT);
  const isInterviewer = () => hasRole(USER_ROLES.INTERVIEWER);
  const isRelationshipManager = () => hasRole(USER_ROLES.RELATIONSHIP_MANAGER);
  const isManager = () => {
    if (!user) return false;
    return hasRole('Semi Agent') && user?.parentAgentId && user?.agentManagerId;
  };
  const isCounselor = () => {
    if (!user) return false;
    return hasRole('Semi Agent') && user?.parentAgentId && !user?.agentManagerId;
  };
  const isSemiAgent = () => isManager() || isCounselor();

  // MOU verification helpers
  // For agents: check their own mouApproved
  // For managers/counselors: need API call (check parent agent's MOU via getMOUStatus)
  const isMOUApproved = () => {
    if (!user) return false;
    // Agents check their own MOU status
    if (isAgent()) {
      return user.mouApproved === true;
    }
    // For managers/counselors, we need to check parent agent's MOU
    // This requires API call, so return null to indicate API check needed
    return null;
  };

  // Role-based access control functions
  const canAccessAdminPortal = () => isAdmin();
  const canAccessSemiAdminPortal = () => isAdmin() || isSemiAdmin();
  const canAccessAgentPortal = () => isAgent();
  const canAccessStudentPortal = () => isStudent();

  // Get user's default dashboard route
  const getUserDashboardRoute = () => {
    if (isAdmin()) return '/admin/dashboard';
    if (isSemiAdmin()) return '/admin/dashboard';
    if (isInterviewer()) return '/interview/dashboard';
    if (isRelationshipManager()) return '/relationship-manager/dashboard';
    if (isAgent()) return '/dashboard';
    if (isManager() || isCounselor()) return '/dashboard';
    return '/dashboard';
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    updateUser,
    logout,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isAdmin,
    isSemiAdmin,
    isAgent,
    isStudent,
    isManager,
    isCounselor,
    isSemiAgent,
    isInterviewer,
    isRelationshipManager,
    canAccessAdminPortal,
    canAccessSemiAdminPortal,
    canAccessAgentPortal,
    canAccessStudentPortal,
    getUserDashboardRoute,
    isMOUApproved,
    USER_ROLES,
    PERMISSIONS
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
